'use client';

import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { SignInButton, useUser } from '@clerk/nextjs';
import { cn } from '@/lib/cn';
import { PortfolioBasics } from '@/components/learn/PortfolioBasics';
import { EtfGuide } from '@/components/learn/EtfGuide';
import { CompoundingCalculator } from '@/components/learn/CompoundingCalculator';

const NAV_ITEMS = [
  { id: 'portfolio-basics', label: 'Portfolio basics' },
  { id: 'etfs', label: 'ETFs' },
  { id: 'compounding', label: 'Compounding' },
];

export default function LearnPage() {
  const { isSignedIn } = useUser();

  return (
    <>
      {/* Anchor nav */}
      <nav
        aria-label="Section navigation"
        className="sticky top-[3.5rem] z-30 backdrop-blur-md"
        style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg) 85%, transparent)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="container mx-auto px-4">
          <ul className="flex items-center gap-1 overflow-x-auto py-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.id} className="flex-shrink-0">
                <a
                  href={`#${item.id}`}
                  className={cn(
                    'inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    'hover:bg-[var(--color-surface)]'
                  )}
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium',
                  'border border-[var(--color-border)]'
                )}
                style={{ color: 'var(--color-accent-muted)', backgroundColor: 'var(--color-surface)' }}
              >
                <Sparkles size={14} />
                New to investing? Start here.
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--color-text)]"
            >
              Investing doesn&apos;t have to be{' '}
              <span style={{ color: 'var(--color-accent-muted)' }}>complicated</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg leading-relaxed max-w-2xl"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Three short sections to give you a working foundation: what a balanced
              portfolio looks like, what ETFs are and why they make diversifying easy,
              and how much your money can grow when you leave it alone.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              {!isSignedIn ? (
                <SignInButton mode="modal">
                  <button
                    className={cn(
                      'inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                      'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                    )}
                  >
                    Create an account
                    <ArrowRight size={16} />
                  </button>
                </SignInButton>
              ) : (
                <Link
                  href="/"
                  className={cn(
                    'inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                    'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                  )}
                >
                  Back to portfolio
                  <ArrowRight size={16} />
                </Link>
              )}
              <a
                href="#portfolio-basics"
                className={cn(
                  'inline-flex items-center px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                  'border border-[var(--color-border)] hover:bg-[var(--color-surface-secondary)]'
                )}
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'transparent' }}
              >
                Continue
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <PortfolioBasics />
      <EtfGuide />
      <CompoundingCalculator />

      {/* Closing CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div
            className="rounded-3xl p-8 sm:p-12 text-center border border-[var(--color-border-subtle)]"
            style={{
              background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 12%, var(--color-surface)) 0%, var(--color-surface) 100%)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">
              Ready to start tracking?
            </h2>
            <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Add your first ETF or stock to EquityLabs and watch your portfolio grow.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              {isSignedIn ? (
                <Link
                  href="/"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
                >
                  Go to my portfolio
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <SignInButton mode="modal">
                  <button className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]">
                    Get started free
                    <ArrowRight size={16} />
                  </button>
                </SignInButton>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
